/**
 * Deep core-API search for positions the lightweight classic model cannot
 * evaluate: games with tile gimmicks (ice/swamp) or unspent character
 * abilities (ninja wallJump, mage removeWall, explorer diagonalStep, and any
 * future {type:'ability', to|at} character). This is the ability-aware
 * counterpart of chooseDeepClassic (src/ai/classic.js): the same
 * iterative-deepening, fixed-root-perspective alpha-beta minimax, but built
 * on the CORE API (legalMoves / applyMove / shortestPathLength) so every
 * ability and gimmick is scored by actually playing it out — no hand-coded
 * per-character logic. Once all ability charges are spent (and there are no
 * gimmicks) the position becomes classic-evaluable again and the fast model
 * search (chooseDeepClassic) takes over — this path only runs while an
 * ability or gimmick is live.
 *
 * Why not just deepen chooseGeneric? The old fallback is an exact 2-ply
 * search whose eval (coreEval) never sees far enough to justify SPENDING an
 * ability: removing a wall that the opponent immediately replaces, or a
 * ninja jump whose payoff is a shorter race, only look good several plies
 * out. This search adds that horizon plus two ability-aware eval terms:
 *
 *   1. ABILITY-HOLDING VALUE (holdTable). An unspent ability is a held asset
 *      exactly like a wall-in-hand: valued in the eval so the search only
 *      spends it when the resulting position (which no longer holds that
 *      value) is genuinely better than keeping it. Per-id weights with a
 *      sane default for unknown ids — the search stays generic as new
 *      characters are added. Too high => never used; too low => wasted; the
 *      values below were measured against the old fallback (see
 *      sim/bench-generic.mjs).
 *
 *   2. NINJA PATH-DISTORTION BLEND (crossBlend). shortestPathLength folds a
 *      ninja's remaining wall-jumps into the BFS as free wall crossings
 *      (core contract §8-8), so a wall placed against a ninja looks useless
 *      to the opponent's eval — it undervalues walls in ninja games. We
 *      cannot change the core semantics, so the eval instead computes BOTH
 *      the jump-aware distance and the plain no-shortcut distance (obtained
 *      generically by momentarily zeroing the player's ability charge — this
 *      only differs for wall-crossing abilities, so it needs no per-character
 *      knowledge) and blends them. The ninja can bypass only ONE wall, not a
 *      chain, so the blended distance keeps walls meaningful.
 *
 * Determinism: fixed iteration order, stable sorts, rng only for final
 * tie-breaks; a machine-independent node budget aborts an in-flight deeper
 * iteration whose partial result is discarded (last completed depth wins).
 */

import {
  legalMoves,
  applyMove,
  pawnMoves,
  shortestPathLength,
  isLegal,
} from '../core/index.js';
import { buildModel, candidateWalls, bfsDistance, bfsPath, wallKey } from './model.js';

const EPS = 1e-9;
const WIN_VALUE = 1e6; // finite so a WIN-vs-WIN comparison stays ordered

function same(a, b) {
  return a === b || Math.abs(a - b) <= EPS;
}

// Per-ability "held asset" weights (value of ONE unspent charge), mirroring
// the walls-in-hand weight. Rationale (measured, sim/bench-generic.mjs):
//   wallJump  ~ a jump erases one wall's detour AND keeps working as a path
//               shortcut until spent; worth a bit more than a raw wall detour.
//   removeWall~ returns a wall to its owner's hand and can undo a detour;
//               option value even when the opponent can replace it.
//   diagonalStep ~ a minor corner-cut / dodge; small.
// Unknown ids (the 5 new characters land here) get a neutral default so the
// search still values holding them and spends them only when a real gain
// appears in the read-out — never a hard-coded per-character rule.
export const DEEP_GENERIC_DEFAULTS = {
  wallWeight: 2.2, // walls-in-hand weight (matches classic hard)
  maxPlies: 4, // total plies root-to-leaf (core is heavy, so shallower than classic)
  tempo: 0.5, // side-to-move first-move bonus in the leaf eval
  // Machine-independent budget = number of core applyMove CLONES the search
  // may spend (the dominant per-node cost), so it directly bounds think time
  // regardless of a node's branching. Much smaller than the classic model
  // search's 4000 node cap because each core clone is far heavier than a model
  // undo. 550 keeps the worst case (late mage games, many removable walls)
  // comfortably inside the browser think-time budget; see sim/bench-generic.
  nodeBudget: 550,
  rootBeam: 8, // only the top-K root moves are searched deep (rest keep their 1-ply value)
  wallPlies: 2, // plies from the root that generate walls/abilities (deeper = pawn-only)
  distCap: 6, // concave distance transform: full up to cap ...
  distTail: 1.0, // ... then excess scaled by tail (1.0 = plain linear, measured best)
  crossBlend: 0.5, // ninja distance blend: 1 = pure jump-aware, 0 = pure no-shortcut
  holdDefault: 1.0, // holding value for an unknown ability charge
  holdTable: {
    wallJump: 1.5,
    removeWall: 1.5,
    diagonalStep: 0.6,
  },
};

/** Concave distance value: full up to cap, then scaled by tail (< 1). */
function distValue(d, cap, tail) {
  return d <= cap ? d : cap + (d - cap) * tail;
}

/**
 * Distance for player `i` to their goal, folding in the ninja path-distortion
 * blend. shortestPathLength already counts wall-jumps as free crossings; we
 * also compute the plain no-shortcut distance by momentarily zeroing the
 * player's ability charge (generic: only wall-crossing abilities change it)
 * and return crossBlend*jumpAware + (1-crossBlend)*noShortcut. Returns -1 if
 * the player is sealed off (never in a legally reached state, but guarded).
 */
function playerDistance(state, i, ctx) {
  const dReal = shortestPathLength(state, i);
  if (dReal < 0) return -1;
  const p = state.players[i];
  // Only wall-crossing abilities can make the two distances differ, and only
  // when walls exist to cross. Skip the second BFS otherwise.
  if (
    ctx.crossBlend >= 1 ||
    state.walls.length === 0 ||
    !p.ability ||
    p.ability.usesLeft <= 0
  ) {
    return dReal;
  }
  const save = p.ability.usesLeft;
  p.ability.usesLeft = 0; // temporarily strip the shortcut (restored below)
  const dNo = shortestPathLength(state, i);
  p.ability.usesLeft = save;
  if (dNo <= dReal) return dReal; // non-crossing ability => identical, no blend
  return ctx.crossBlend * dReal + (1 - ctx.crossBlend) * dNo;
}

/** Total held-ability value for player `i` (unspent charges * per-id weight). */
function abilityHold(state, i, ctx) {
  const p = state.players[i];
  if (!p.ability || p.ability.usesLeft <= 0) return 0;
  const w = ctx.holdTable[p.ability.id];
  return p.ability.usesLeft * (w === undefined ? ctx.holdDefault : w);
}

/** Swamp tempo correction (a swamped player loses a movement turn). */
function swampBonus(state, me) {
  let bonus = 0;
  const opp = 1 - me;
  if (state.players[opp].effects && state.players[opp].effects.swamped) bonus += 1;
  if (state.players[me].effects && state.players[me].effects.swamped) bonus -= 1;
  return bonus;
}

/**
 * Static eval ALWAYS from the fixed root player's perspective (ctx.rootMe) —
 * a minimax eval, not negamax, on purpose (concave on the opponent's
 * distance, linear on my own; see chooseDeepClassic for why the asymmetry
 * matters). Adds walls-in-hand, ability-holding value, swamp tempo and a
 * side-to-move tempo bonus.
 */
function staticEval(state, ctx) {
  const me = ctx.rootMe;
  const opp = 1 - me;
  const dMe = playerDistance(state, me, ctx);
  if (dMe < 0) return -WIN_VALUE;
  const dOpp = playerDistance(state, opp, ctx);
  if (dOpp < 0) return WIN_VALUE;
  const tempo = state.current === me ? ctx.tempo : -ctx.tempo;
  return (
    distValue(dOpp, ctx.cap, ctx.tail) -
    dMe +
    ctx.ww * (state.players[me].wallsLeft - state.players[opp].wallsLeft) +
    abilityHold(state, me, ctx) -
    abilityHold(state, opp, ctx) +
    swampBonus(state, me) +
    tempo
  );
}

/**
 * Is removing the wall at (col,row,orientation) worth SEARCHING for `side`?
 * A removal only helps by (a) shortening the mover's own path or (b) returning
 * one of the mover's OWN walls to hand. Every other removal just hands the
 * board back unchanged (or a wall to the opponent) and is never a best move,
 * so it is pruned before the expensive clone+eval — this is what tames the
 * removeWall branching (one candidate per placed wall) in late mage games.
 */
function removalWorthSearching(state, model, side, col, row, orientation, dist0) {
  const key = wallKey(col, row, orientation);
  if (!model.wallSet.has(key)) return false; // not actually placed (defensive)
  // Own wall: reclaiming it (+1 wall in hand) is always worth a look.
  for (const w of state.walls) {
    if (w.col === col && w.row === row && w.orientation === orientation) {
      if (w.owner === side) return true;
      break;
    }
  }
  // Otherwise keep only if removing it shortens the mover's shortest path.
  model.wallSet.delete(key);
  const d = bfsDistance(model.size, model.wallSet, model.pawns[side], model.goalRows[side]);
  model.wallSet.add(key);
  return d >= 0 && d < dist0;
}

/**
 * Children at a "shallow" node (walls + abilities allowed): the exact core
 * legal moves, pruned to keep the per-child clone+eval count small —
 *   - pawn moves: kept in full (few);
 *   - wall moves: only anchors near a player's shortest path (candidateWalls);
 *   - wall-targeting abilities (an `at` field — removeWall and any future
 *     wall-stealing ability): only removals that shorten the mover or reclaim
 *     its own wall (see removalWorthSearching);
 *   - cell-targeting abilities (a `to` field — wallJump / diagonalStep / the
 *     new {type:'ability',to} characters): kept in full (few candidates).
 * The pruning is by move SHAPE, never by ability id, so it stays generic as
 * new characters are added. `pre` is a precomputed legalMoves list (root
 * reuses it); pass null to compute here.
 */
function shallowMoves(state, pre) {
  const all = pre || legalMoves(state);
  const side = state.current;
  const model = buildModel(state);
  const anchors = new Set();
  for (const w of candidateWalls(model)) anchors.add(wallKey(w.col, w.row, w.orientation));
  let dist0 = -1; // lazily computed mover distance for removal pruning
  const out = [];
  for (const mv of all) {
    if (mv.type === 'pass') continue; // never voluntary
    if (mv.type === 'wall') {
      if (anchors.has(wallKey(mv.at.col, mv.at.row, mv.orientation))) out.push(mv);
    } else if (mv.type === 'ability' && mv.at) {
      if (dist0 < 0) {
        dist0 = bfsDistance(model.size, model.wallSet, model.pawns[side], model.goalRows[side]);
      }
      if (removalWorthSearching(state, model, side, mv.at.col, mv.at.row, mv.orientation, dist0)) {
        out.push(mv);
      }
    } else {
      out.push(mv); // pawn move or cell-targeting ability
    }
  }
  return out;
}

/**
 * Cheap children for an INTERIOR shallow node: pawn moves + walls that block
 * the non-mover's shortest path (blocking + corner orientation), validated by
 * core isLegal. Unlike shallowMoves this does NOT call the core legalMoves
 * (~one path-check per board anchor) or regenerate ability moves — the root
 * already committed the ability decision, and the ONE interior effect that
 * must survive is the opponent's re-wall reply (so a removeWall the opponent
 * simply replaces is correctly seen as a poor move). Dropping opponent ability
 * re-replies here is the approximation that keeps late, wall-dense positions
 * inside the browser think-time budget.
 */
function interiorShallowMoves(state) {
  const side = state.current;
  const model = buildModel(state);
  const out = [];
  for (const to of pawnMoves(state, side)) out.push({ type: 'move', to });
  if (state.players[side].wallsLeft <= 0) return out;
  const opp = 1 - side;
  const path = bfsPath(model.size, model.wallSet, model.pawns[opp], model.goalRows[opp]);
  if (!path) return out;
  const seen = new Set();
  const size = model.size;
  const add = (col, row, orientation) => {
    if (col < 0 || row < 0 || col > size - 2 || row > size - 2) return;
    const key = wallKey(col, row, orientation);
    if (seen.has(key)) return;
    seen.add(key);
    const mv = { type: 'wall', at: { col, row }, orientation };
    if (isLegal(state, mv)) out.push(mv);
  };
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a.col === b.col) {
      const r = Math.min(a.row, b.row);
      add(a.col - 1, r, 'h');
      add(a.col, r, 'h');
      add(a.col - 1, r, 'v');
      add(a.col, r, 'v');
    } else {
      const c = Math.min(a.col, b.col);
      add(c, a.row - 1, 'v');
      add(c, a.row, 'v');
      add(c, a.row - 1, 'h');
      add(c, a.row, 'h');
    }
  }
  return out;
}

/**
 * applyMove wrapped in the node-budget counter. The budget counts CLONES
 * (core applyMove deep-clones the state, the dominant per-node cost), so it
 * directly bounds think time regardless of a node's branching fan-out — a
 * shallow node that expands 15 children spends 15 budget, not 1. Returns null
 * once the budget is exhausted (the whole in-flight iteration is then
 * discarded, so a null-poisoned partial value never reaches the final pick).
 */
function applyCounted(state, mv, ctx) {
  if (ctx.nodes >= ctx.budget) {
    ctx.aborted = true;
    return null;
  }
  ctx.nodes += 1;
  return applyMove(state, mv);
}

/**
 * Alpha-beta minimax value of `state` with `depth` plies left, from the fixed
 * root perspective (ctx.rootMe). MAX nodes are rootMe's turn, MIN the
 * opponent's. depth <= 0 is a leaf (static eval). Shallow nodes (depth >=
 * wallMinDepth) branch over walls + abilities; deeper nodes are pawn-only
 * (cheap race look-ahead). Wins score +/-(WIN + depth) so sooner beats later.
 */
function deepSearch(state, depth, alpha, beta, ctx) {
  if (ctx.aborted) return 0;
  if (state.winner !== null) {
    // Reached only if a prior ply already won; sign by who won.
    return state.winner === ctx.rootMe ? WIN_VALUE + depth : -(WIN_VALUE + depth);
  }
  if (depth <= 0) return staticEval(state, ctx);

  const side = state.current;
  const isMax = side === ctx.rootMe;
  const goal = state.players[side].goalRow;
  const shallow = depth >= ctx.wallMinDepth;

  if (shallow) {
    // Cheap interior move set (pawn + opponent re-wall), applied + ordered.
    const moves = interiorShallowMoves(state);
    if (moves.length === 0) return staticEval(state, ctx);
    const kids = [];
    for (const mv of moves) {
      const next = applyCounted(state, mv, ctx);
      if (next === null) break; // budget hit: iteration will be discarded
      if (next.winner === side) {
        // A winning move dominates for `side`.
        return isMax ? WIN_VALUE + depth : -(WIN_VALUE + depth);
      }
      kids.push({ next, s: staticEval(next, ctx) });
    }
    if (ctx.aborted) return 0;
    kids.sort((a, b) => (isMax ? b.s - a.s : a.s - b.s)); // stable
    if (isMax) {
      let best = -Infinity;
      for (const k of kids) {
        const v = deepSearch(k.next, depth - 1, alpha, beta, ctx);
        if (v > best) best = v;
        if (best > alpha) alpha = best;
        if (alpha >= beta) break;
      }
      return best;
    }
    let best = Infinity;
    for (const k of kids) {
      const v = deepSearch(k.next, depth - 1, alpha, beta, ctx);
      if (v < best) best = v;
      if (best < beta) beta = best;
      if (alpha >= beta) break;
    }
    return best;
  }

  // Deep node: pawn-only, ordered by a cheap advancement key (no BFS), moves
  // applied lazily so alpha-beta cutoffs avoid the clone.
  const dests = pawnMoves(state, side);
  if (dests.length === 0) return staticEval(state, ctx); // swamped/stuck: approximate
  const fromRow = state.players[side].pawn.row;
  const goalDist0 = Math.abs(fromRow - goal);
  const ordered = [];
  for (const to of dests) {
    if (to.row === goal) {
      // `side` can step onto the goal now: a win for `side`.
      return isMax ? WIN_VALUE + depth : -(WIN_VALUE + depth);
    }
    const adv = goalDist0 - Math.abs(to.row - goal);
    ordered.push({ to, key: isMax ? adv : -adv });
  }
  ordered.sort((a, b) => b.key - a.key); // best-for-side first (key already signed)
  if (isMax) {
    let best = -Infinity;
    for (const o of ordered) {
      const next = applyCounted(state, { type: 'move', to: o.to }, ctx);
      if (next === null) break;
      const v = deepSearch(next, depth - 1, alpha, beta, ctx);
      if (v > best) best = v;
      if (best > alpha) alpha = best;
      if (alpha >= beta) break;
    }
    return best;
  }
  let best = Infinity;
  for (const o of ordered) {
    const next = applyCounted(state, { type: 'move', to: o.to }, ctx);
    if (next === null) break;
    const v = deepSearch(next, depth - 1, alpha, beta, ctx);
    if (v < best) best = v;
    if (best < beta) beta = best;
    if (alpha >= beta) break;
  }
  return best;
}

/**
 * Iterative-deepening alpha-beta minimax over the core API. Drop-in for
 * chooseGeneric on ability/gimmick positions. opts overrides
 * DEEP_GENERIC_DEFAULTS.
 */
export function chooseDeepGeneric(state, rng, opts = {}) {
  const cfg = { ...DEEP_GENERIC_DEFAULTS, ...opts };
  const me = state.current;

  // Search on a history-stripped copy of the state. core applyMove clones the
  // WHOLE state (including the move history, which grows one entry per ply) on
  // every node; in a long game that clone dominates the node cost. The history
  // is irrelevant to move selection, so blanking it once here makes every one
  // of the search's downstream clones cheap. The original `state` is untouched
  // (this is a fresh deep clone) and the final move is re-validated against it.
  const root = structuredClone(state);
  root.history = [];

  const moves = legalMoves(root);
  if (moves.length === 1) return moves[0]; // forced (e.g. pass)

  const ctx = {
    rootMe: me,
    ww: cfg.wallWeight,
    tempo: cfg.tempo,
    cap: cfg.distCap,
    tail: cfg.distTail,
    crossBlend: cfg.crossBlend,
    holdTable: cfg.holdTable,
    holdDefault: cfg.holdDefault,
    nodes: 0,
    budget: cfg.nodeBudget,
    aborted: false,
    // Deepest ply (in `depth` units) that still generates walls/abilities.
    wallMinDepth: Math.max(2, cfg.maxPlies - cfg.wallPlies + 1),
  };
  const rootBeam = cfg.rootBeam;

  // Root candidates: pruned-wall + full pawn/ability set, each pre-applied
  // (reused across deepening iterations) with a 1-ply static score.
  const rootMoves = shallowMoves(root, moves);
  const scored = [];
  const wins = [];
  for (const mv of rootMoves) {
    const next = applyMove(root, mv);
    if (next.winner === me) {
      wins.push(mv);
      continue;
    }
    scored.push({ mv, next, s1: staticEval(next, ctx), val: 0 });
  }
  if (wins.length > 0) return wins[Math.floor(rng() * wins.length)];
  if (scored.length === 0) return moves[Math.floor(rng() * moves.length)];

  scored.sort((a, b) => b.s1 - a.s1); // stable -> deterministic first ordering
  for (const it of scored) it.val = it.s1; // seed for the depth-1 fallback
  // Only the top-K root moves are searched deep (core is heavy); the rest keep
  // their strictly-lower 1-ply score and cannot win the final pick. This bounds
  // the number of expensive shallow interior (opponent-reply) nodes.
  let order = scored.slice(0, Math.max(1, rootBeam));

  for (let plies = 2; plies <= cfg.maxPlies; plies++) {
    const searchDepth = plies - 1; // plies below the applied root move
    const iter = order.map((it) => ({ ...it }));
    let best = -Infinity;
    for (const item of iter) {
      // After my root move it is the opponent's turn (a MIN node inside
      // deepSearch, since next.current !== rootMe). Aspiration lower bound so
      // ties keep exact values for a fair rng tie-break.
      const alpha = best === -Infinity ? -Infinity : best - EPS;
      const v = deepSearch(item.next, searchDepth, alpha, Infinity, ctx);
      if (ctx.aborted) break;
      item.val = v;
      if (v > best) best = v;
    }
    if (ctx.aborted) break; // budget hit mid-iteration: keep previous depth
    iter.sort((a, b) => b.val - a.val || b.s1 - a.s1); // stable
    order = iter;
  }

  let best = order[0];
  for (const item of order) {
    if (!same(item.val, best.val)) {
      if (item.val > best.val) best = item;
      continue;
    }
    if (!same(item.s1, best.s1) && item.s1 > best.s1) best = item;
  }
  const tied = order.filter((i) => same(i.val, best.val) && same(i.s1, best.s1));
  const chosen = tied[Math.floor(rng() * tied.length)];
  if (!isLegal(state, chosen.mv)) {
    throw new Error('AI produced an illegal move: ' + JSON.stringify(chosen.mv));
  }
  return chosen.mv;
}
